import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import PageWrapper from "../../../../components/layout/page/PageWrapper";
import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";
import TextField from "../../../../components/input/textfield/TextField";
import Button from "../../../../components/buttons/Button";
import StatusAlert from "../../../../components/alerts/StatusAlert";
import FeedbackStatus from "../../../../components/feedback/FeedbackStatus";

import { useSignUp } from "../../hooks/useSignUp";

import "./SignUpPage.css";

export default function SignUpPage() {
  const navigate = useNavigate();
  const { formData, status, handleChange, handleSubmit } = useSignUp();

  useEffect(() => {
    document.title = "Katch | Sign Up";
  }, []);

  const onSubmit = (event) => {
    event.preventDefault();

    if (!status.loading) {
      handleSubmit();
    }
  };

  return (
    <PageWrapper className="sign-up-wrapper">
      <main className="sign-up-page">
        <div className="sign-up-page__content">
          {status.success ? (
            <FeedbackStatus
              title="Verify your email"
              description={
                <span>
                  We've sent a verification link to{" "}
                  <strong>{formData.email || "your email"}</strong>. Check your
                  inbox or spam folder to activate your account.
                </span>
              }
              buttonText="Back to Sign in"
              onAction={() => navigate("/sign-in")}
            />
          ) : (
            <>
              <div className="sign-up-page__header">
                <Heading as="h1" size="lg">
                  Create account
                </Heading>

                <Text as="p" size="sm" tone="secondary">
                  Join Katch and connect with your sports community.
                </Text>
              </div>

              <form className="sign-up-page__form" onSubmit={onSubmit}>
                {status.message && <StatusAlert status={status} />}

                <div className="sign-up-page__fields">
                  <div className="sign-up-page__name-fields">
                    <TextField
                      name="firstName"
                      label="First name"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="First name"
                      autoComplete="given-name"
                      required
                    />

                    <TextField
                      name="lastName"
                      label="Last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Last name"
                      autoComplete="family-name"
                      required
                    />
                  </div>

                  <TextField
                    name="email"
                    type="email"
                    label="Email address"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    autoComplete="email"
                    required
                  />

                  <TextField
                    name="password"
                    type="password"
                    label="Password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    showPasswordOption
                    required
                  />
                </div>

                <label className="sign-up-page__agreement">
                  <input
                    type="checkbox"
                    name="agreement"
                    checked={Boolean(formData.agreement)}
                    onChange={handleChange}
                    required
                  />

                  <span>
                    I agree to the{" "}
                    <Link
                      to="/u/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>

                <Button
                  type="submit"
                  value={
                    status.loading ? "Creating account..." : "Create account"
                  }
                  variant="primary"
                  disabled={status.loading}
                />
              </form>

              <div className="sign-up-page__footer">
                <Text as="p" size="sm" tone="secondary">
                  Already have an account?{" "}
                  <Link to="/sign-in" className="sign-up-page__link">
                    Sign in
                  </Link>
                </Text>
              </div>
            </>
          )}
        </div>
      </main>
    </PageWrapper>
  );
}
